import { useParams, Link } from "react-router-dom";
import { comics } from "@/mocks/comics";
import { siteInfo } from "@/mocks/comics";

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <main className="pt-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，没有找到该韩国漫画的推荐信息。</p>
          <Link
            to="/"
            className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer no-underline"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${comic.title} - 韩国漫画推荐`,
    description: comic.description,
    author: { "@type": "Person", name: comic.author.split(" / ")[0] },
    datePublished: comic.updateDate,
    dateModified: comic.updateDate,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://koreancomics.cn/article/${comic.id}`,
    },
    publisher: {
      "@type": "Organization",
      name: siteInfo.name,
    },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://koreancomics.cn/" },
      {
        "@type": "ListItem",
        position: 2,
        name: comic.title,
        item: `https://koreancomics.cn/article/${comic.id}`,
      },
    ],
  };

  return (
    <main>
      <article className="pt-20 pb-14">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <header className="mb-8">
            <div className="flex flex-wrap gap-2 mb-4">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full"
                >
                  {tag}
                </span>
              ))}
              <span
                className={`text-xs px-2.5 py-1 rounded-full ${
                  comic.status === "已完结"
                    ? "bg-accent-100 text-accent-700"
                    : "bg-primary-100 text-primary-700"
                }`}
              >
                {comic.status}
              </span>
            </div>
            <h1
              className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {comic.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                {comic.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-yellow-400 w-4 h-4 flex items-center justify-center"></i>
                <strong className="text-primary-600">{comic.rating}</strong>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                {(comic.views / 10000).toFixed(0)}万阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-file-list-3-line w-4 h-4 flex items-center justify-center"></i>
                {comic.chapters}话
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                更新于 {comic.updateDate}
              </span>
            </div>
          </header>

          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="flex-shrink-0">
              <img
                src={comic.cover}
                alt={`${comic.title} - 韩国漫画推荐封面`}
                title={`${comic.title} - 韩国漫画推荐`}
                className="w-48 md:w-56 rounded-xl object-cover object-top shadow-sm"
                style={{ aspectRatio: "3/4" }}
              />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                作品简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                {comic.description}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/category/${comic.category}`}
                  className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer no-underline whitespace-nowrap"
                >
                  查看更多{comic.tags[0]}类韩国漫画
                </Link>
                <Link
                  to="/"
                  className="px-4 py-2 border border-background-200/70 text-foreground-600 rounded-full text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer no-underline whitespace-nowrap"
                >
                  返回首页
                </Link>
              </div>
            </div>
          </div>

          {relatedComics.length > 0 && (
            <section className="border-t border-background-200/70 pt-10">
              <h2
                className="text-xl font-bold text-foreground-950 mb-6"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                相关韩国漫画推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedComics.map((c) => (
                  <Link
                    key={c.id}
                    to={`/article/${c.id}`}
                    className="group flex flex-col bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:shadow-sm transition-all cursor-pointer no-underline"
                  >
                    <div className="relative overflow-hidden aspect-[3/4]">
                      <img
                        src={c.cover}
                        alt={`${c.title} - 韩国漫画`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-sm text-foreground-950 group-hover:text-primary-700 transition-colors line-clamp-1">
                        {c.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-1 flex items-center gap-1">
                        <i className="ri-star-fill text-yellow-400 w-3 h-3 flex items-center justify-center"></i>
                        {c.rating}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
    </main>
  );
}